"use client";

import { Capacitor } from "@capacitor/core";
import { useEffect } from "react";
import { useCurrentLocation } from "@/hooks/use-current-location";
import { HiivePushBridge } from "@/lib/native/hiive-push-bridge";
import { clearIosChatNotifications } from "@/lib/native/hiive-push-permissions";

/**
 * Reports the chat the user is currently viewing to native, so
 * HiiveMessagingService can suppress a push for that one conversation while the
 * app is foregrounded (polling already renders the message on screen) — every
 * other message still renders natively, WhatsApp-style.
 */
/** Pull the numeric chat id out of a `/chat/123` pathname (else null). */
const chatIdFromPathname = (pathname: string | null): string | null => {
  if (!pathname) return null;
  const match = pathname.match(/^\/chat\/([^/?#]+)/);
  return match ? match[1] : null;
};

export function useActiveChatBridge() {
  const { pathname } = useCurrentLocation();

  useEffect(() => {
    const platform = Capacitor.getPlatform();
    const chatId = chatIdFromPathname(pathname);
    if (platform === "ios") {
      if (!chatId) return;
      const clear = () => {
        if (document.visibilityState === "visible")
          void clearIosChatNotifications(chatId).catch(() => {});
      };
      clear();
      document.addEventListener("visibilitychange", clear);
      return () => document.removeEventListener("visibilitychange", clear);
    }
    if (platform !== "android") return;

    if (chatId) {
      void HiivePushBridge.setActiveChat({ chatId }).catch(() => {});
      return () => {
        void HiivePushBridge.clearActiveChat().catch(() => {});
      };
    }

    void HiivePushBridge.clearActiveChat().catch(() => {});
  }, [pathname]);
}
